import { useState } from 'react';

export default function PracticalChecklist() {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const checklistSections = [
    {
      title: 'Sistemas Energéticos',
      items: [
        'Ajusto los descansos según el objetivo: 3-5min para fuerza, 60-120s para hipertrofia, 30-60s para resistencia',
        'Entiendo qué sistema energético domina en cada tipo de entrenamiento y lo programo específicamente',
        'No hago cardio intenso antes de entrenar fuerza para no agotar el sistema glucolítico',
        'Uso cardio de baja intensidad (zona 2) para mejorar la recuperación entre sesiones',
        'Sé que el ATP-PC se recupera 95% en 3-5 minutos, por eso descanso suficiente en fuerza máxima'
      ]
    },
    {
      title: 'Reclutamiento y Fibras Musculares',
      items: [
        'Incluyo trabajo pesado (3-6 reps) para reclutar fibras tipo II por alta demanda de fuerza',
        'Incluyo trabajo moderado al fallo (8-12 reps) para reclutar fibras tipo II por fatiga acumulada',
        'Ocasionalmente incluyo trabajo explosivo (saltos, lanzamientos) para mejorar la tasa de reclutamiento',
        'Entiendo que las primeras semanas de entrenamiento las ganancias son neurales, no musculares',
        'Si uso cargas ligeras (&lt;60%), las llevo cerca del fallo para reclutar fibras de alto umbral'
      ]
    },
    {
      title: 'Fatiga y Recuperación',
      items: [
        'Distingo entre fatiga periférica (músculo cansado) y central (desmotivación, todo se siente pesado)',
        'Monitoreo señales de fatiga central: calidad del sueño, motivación, velocidad de la barra',
        'Programo deloads cada 4-6 semanas: reduzco volumen 50% o intensidad 20-30% durante 1 semana',
        'Duermo 7-9 horas no negociables para recuperación neural y síntesis proteica',
        'Si detecto fatiga central, hago un deload inmediato en lugar de seguir entrenando pesado'
      ]
    },
    {
      title: 'Hipertrofia y Programación',
      items: [
        'Priorizo tensión mecánica: cargas 70-85%, control excéntrico, ROM completo, cerca del fallo',
        'Uso estrés metabólico como complemento: 12-20 reps, descansos cortos, en ejercicios de aislamiento',
        'No busco daño muscular activamente: el DOMS no es indicador de un buen entrenamiento',
        'Ajusto la frecuencia según mi nivel: principiante (2-3x/semana), avanzado (1-2x/semana)',
        'Como en superávit calórico (+200-500 kcal/día) y consumo 1.6-2.2g proteína/kg peso corporal'
      ]
    }
  ];

  const totalItems = checklistSections.reduce((sum, section) => sum + section.items.length, 0);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const progress = Math.round((checkedCount / totalItems) * 100);

  const toggleItem = (sectionIndex: number, itemIndex: number) => {
    const key = `${sectionIndex}-${itemIndex}`;
    setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-br from-[#1A3A2E] to-[#0D1F17] rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-checkbox-multiple-line text-3xl text-[#39FF14]"></i>
          <h2 className="text-3xl font-bold">Checklist Práctico del Módulo 2</h2>
        </div>
        <p className="text-gray-200 text-lg leading-relaxed mb-6">
          Verifica que estás aplicando correctamente los conceptos de fisiología del ejercicio en tu entrenamiento. Marca cada punto que ya implementas.
        </p>
        <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progreso de Implementación</span>
            <span className="text-sm font-bold text-[#39FF14]">{checkedCount}/{totalItems} ({progress}%)</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3">
            <div 
              className="bg-[#39FF14] h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {checklistSections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="bg-white rounded-2xl p-8 border border-gray-200">
          <h3 className="text-xl font-bold text-black mb-6 flex items-center gap-3">
            <div className="w-8 h-8 bg-[#39FF14] rounded-full flex items-center justify-center">
              <span className="text-black text-sm font-bold">{sectionIndex + 1}</span>
            </div>
            {section.title}
          </h3>
          <div className="space-y-3">
            {section.items.map((item, itemIndex) => {
              const key = `${sectionIndex}-${itemIndex}`;
              const isChecked = checkedItems[key] || false;
              return (
                <label
                  key={itemIndex}
                  className="flex items-start gap-3 p-4 rounded-xl bg-[#F5F2EB] hover:bg-[#39FF14]/10 transition-all cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleItem(sectionIndex, itemIndex)}
                    className="w-5 h-5 mt-0.5 accent-[#39FF14] cursor-pointer flex-shrink-0"
                  />
                  <span className={`text-sm leading-relaxed ${isChecked ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                    {item}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {progress === 100 && (
        <div className="bg-[#39FF14] rounded-2xl p-8 text-center">
          <i className="ri-trophy-line text-5xl text-black mb-4"></i>
          <h3 className="text-2xl font-bold text-black mb-2">¡Checklist Completo!</h3>
          <p className="text-black">
            Estás aplicando correctamente todos los principios de fisiología del ejercicio. Continúa al siguiente módulo.
          </p>
        </div>
      )}
    </div>
  );
}